(() => {
  'use strict';

  if (window.__OSTUKLIKK_QUICK_ADD_LITE__) {
    return;
  }

  window.__OSTUKLIKK_QUICK_ADD_LITE__ = true;

  const BUTTON_SELECTOR = '[data-quick-add-lite]';

  function getRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function getLabel(button, key, fallback) {
    const value = button?.dataset?.[key];
    const locale = document.documentElement.lang.split('-')[0];
    const labels = {
      et: {labelDefault: 'Lisa ostukorvi', labelAdding: 'Lisan...', labelAdded: 'Lisatud', labelError: 'Proovi uuesti'},
      fi: {labelDefault: 'Lisää ostoskoriin', labelAdding: 'Lisätään...', labelAdded: 'Lisätty', labelError: 'Yritä uudelleen'},
      ru: {labelDefault: 'В корзину', labelAdding: 'Добавление...', labelAdded: 'Добавлено', labelError: 'Попробуйте снова'}
    };
    return value || labels[locale]?.[key] || fallback;
  }

  function setButtonState(button, state) {
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    const labels = {
      idle: getLabel(button, 'labelDefault', 'Add to cart'),
      loading: getLabel(button, 'labelAdding', 'Adding...'),
      added: getLabel(button, 'labelAdded', 'Added'),
      error: getLabel(button, 'labelError', 'Try again')
    };

    button.dataset.state = state;

    const accessibleLabel = labels[state] || labels.idle;
    button.setAttribute('aria-label', accessibleLabel);
    button.setAttribute('title', accessibleLabel);

    const status = button.querySelector('[data-quick-add-lite-status]');
    if (status) {
      status.textContent = accessibleLabel;
    }
  }

  function readVisibleCartCount() {
    const countElement = document.querySelector(
      'cart-icon .cart-bubble__text-count'
    );

    if (!countElement) {
      return null;
    }

    const raw = String(countElement.textContent || '').replace(/[^0-9]/g, '');
    if (!raw) {
      return null;
    }

    const count = Number(raw);
    return Number.isFinite(count) ? count : null;
  }

  function writeCartCount(count) {
    const safeCount = Math.max(0, Number(count) || 0);

    document.querySelectorAll('cart-icon').forEach((cartIcon) => {
      try {
        if ('currentCartCount' in cartIcon) {
          cartIcon.currentCartCount = safeCount;
        }
      } catch (error) {
        // The DOM fallback below still updates the visible badge.
      }

      cartIcon.classList.toggle(
        'header-actions__cart-icon--has-cart',
        safeCount > 0
      );

      const bubble = cartIcon.querySelector('.cart-bubble');
      const countElement = cartIcon.querySelector('.cart-bubble__text-count');

      if (!bubble) {
        return;
      }

      if (safeCount > 0) {
        bubble.classList.remove('visually-hidden');
        bubble.removeAttribute('hidden');
        bubble.hidden = false;

        if (countElement) {
          countElement.textContent = String(safeCount);
          countElement.classList.remove('hidden');
          countElement.removeAttribute('hidden');
          countElement.hidden = false;
          countElement.setAttribute('aria-hidden', 'false');
        }
      } else {
        bubble.classList.add('visually-hidden');

        if (countElement) {
          countElement.textContent = '';
          countElement.classList.add('hidden');
          countElement.setAttribute('aria-hidden', 'true');
        }
      }
    });
  }

  async function refreshExactCartCount() {
    const response = await fetch(`${getRoot()}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Cart count refresh failed');
    }

    const cart = await response.json();
    const count = Number(cart?.item_count);

    if (Number.isFinite(count)) {
      writeCartCount(count);
    }

    return cart;
  }


  function refreshVisibleCartPage(cart) {
    const path = String(window.location.pathname || '').replace(/\/$/, '');

    if (!path.endsWith('/cart')) {
      return;
    }

    const itemCount = Number(cart?.item_count);
    const safeItemCount = Number.isFinite(itemCount) ? itemCount : undefined;

    const result = {
      cart: {
        totalQuantity: safeItemCount
      },
      detail: {
        sections: {},
        itemCount: safeItemCount,
        source: 'quick-add-lite',
        didError: false
      }
    };

    const event = new CustomEvent('shopify:cart:lines-update', {
      bubbles: true,
      detail: {
        action: 'add',
        source: 'quick-add-lite',
        itemCount: safeItemCount
      }
    });

    Object.defineProperty(event, 'promise', {
      configurable: true,
      value: Promise.resolve(result)
    });

    document.dispatchEvent(event);
  }

  async function addVariant(button, boostVariantId, sellingPlan) {
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }

    if (button.dataset.quickAddBusy === 'true') {
      return;
    }

    const variantId = Number(boostVariantId || button.dataset.variantId);
    if (!variantId) {
      return;
    }

    button.dataset.quickAddBusy = 'true';
    button.disabled = true;
    setButtonState(button, 'loading');

    try {
      const response = await fetch(`${getRoot()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              ...(sellingPlan ? {selling_plan: sellingPlan} : {})
            }
          ]
        })
      });

      if (!response.ok) {
        throw new Error('Quick add failed');
      }

      /*
       * Instant visual feedback: if the current badge already contains a
       * trustworthy number, increase it immediately. The exact Shopify cart
       * count is verified straight afterwards with one small /cart.js request.
       */
      const visibleCount = readVisibleCartCount();
      if (Number.isFinite(visibleCount)) {
        writeCartCount(visibleCount + 1);
      }

      setButtonState(button, 'added');

      let refreshedCart = null;

      try {
        refreshedCart = await refreshExactCartCount();
      } catch (error) {
        // Adding succeeded. A later native cart update / wishlist fallback can
        // still correct the badge if this optional count refresh fails.
        console.warn('Quick Add Lite cart count refresh failed.', error);
      }

      // On the full cart page, tell Horizon that cart lines changed.
      // The cart-items component then refreshes its own section, so the newly
      // added product, totals and quantities appear without a manual page reload.
      refreshVisibleCartPage(refreshedCart);

      window.setTimeout(() => {
        button.dataset.quickAddBusy = 'false';
        button.disabled = false;
        setButtonState(button, 'idle');
      }, 900);
    } catch (error) {
      console.warn('Quick Add Lite could not add product to cart.', error);

      setButtonState(button, 'error');

      window.setTimeout(() => {
        button.dataset.quickAddBusy = 'false';
        button.disabled = false;
        setButtonState(button, 'idle');
      }, 1500);
    }
  }

  window.okQuickAddLite = addVariant;

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const button = event.target.closest(BUTTON_SELECTOR);
      if (!button) {
        return;
      }

      /*
       * Capture phase only for the actual Quick Add Lite button.
       * This prevents the product-card overlay link from navigating,
       * without touching any normal product-card or wishlist clicks.
       */
      event.preventDefault();
      event.stopPropagation();

      addVariant(button);
    },
    true
  );
})();

